import React from 'react';
import { Link } from 'react-router-dom';
import PaintingHeader from './PaintingHeader';
import PaintingFooter from './PaintingFooter';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { 
  Home as HomeIcon, 
  Building2, 
  Paintbrush, 
  Droplets,
  Package,
  Wrench,
  Palette,
  CheckCircle,
  Phone
} from 'lucide-react';

const Services = () => {
  const services = [
    {
      icon: <HomeIcon className="w-12 h-12" />,
      title: "Residential Interior Painting",
      description: "Transform your home with our expert interior painting services. We specialize in living rooms, bedrooms, kitchens, bathrooms, and more.",
      features: [
        "Premium quality paints",
        "Color consultation included",
        "Furniture protection",
        "Clean, precise lines",
        "Same-day cleanup",
        "Satisfaction guaranteed"
      ],
      image: "https://images.unsplash.com/photo-1724582586529-62622e50c0b3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsaXZpbmclMjByb29tJTIwZnJlc2glMjBwYWludHxlbnwwfHx8fDE3ODQxMzUwOTR8MA&ixlib=rb-4.1.0&q=85"
    },
    {
      icon: <Building2 className="w-12 h-12" />,
      title: "Residential Exterior Painting",
      description: "Protect and beautify your home's exterior with weather-resistant, high-quality paints designed for South Florida's climate.",
      features: [
        "Weather-resistant coatings",
        "Pressure washing included",
        "Surface preparation",
        "UV-protected finishes",
        "Hurricane-ready products",
        "5-year warranty"
      ],
      image: "https://images.unsplash.com/photo-1638945657536-60259aae3c34?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBob21lJTIwZXh0ZXJpb3IlMjBzb3V0aCUyMGZsb3JpZGF8ZW58MHx8fHwxNzg0MTM1MDk0fDA&ixlib=rb-4.1.0&q=85"
    },
    {
      icon: <Paintbrush className="w-12 h-12" />,
      title: "Commercial Painting",
      description: "Professional painting services for offices, retail spaces, restaurants, and commercial properties. Flexible scheduling to minimize disruption.",
      features: [
        "After-hours scheduling",
        "Minimal business disruption",
        "Large-scale projects",
        "Industrial-grade materials",
        "Licensed & insured",
        "Volume discounts available"
      ],
      image: "https://images.unsplash.com/photo-1739820460102-1efe9165997f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBob21lJTIwZXh0ZXJpb3IlMjBzb3V0aCUyMGZsb3JpZGF8ZW58MHx8fHwxNzg0MTM1MDk0fDA&ixlib=rb-4.1.0&q=85"
    },
    {
      icon: <Package className="w-12 h-12" />,
      title: "Cabinet Painting & Refinishing",
      description: "Give your kitchen or bathroom a complete makeover without the cost of replacement. Expert cabinet painting and refinishing.",
      features: [
        "Custom color matching",
        "Durable finishes",
        "Hardware reinstallation",
        "Smooth, factory-like finish",
        "Cost-effective upgrade",
        "Quick turnaround"
      ],
      image: "https://images.unsplash.com/photo-1556912173-46c336c7fd55?w=600&h=400&fit=crop"
    },
    {
      icon: <Droplets className="w-12 h-12" />,
      title: "Deck Staining & Sealing",
      description: "Protect your outdoor wood surfaces from Florida's harsh sun and moisture. Professional staining and sealing services.",
      features: [
        "Premium wood stains",
        "UV protection",
        "Moisture resistance",
        "Surface cleaning included",
        "Multiple finish options",
        "Long-lasting protection"
      ],
      image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600&h=400&fit=crop"
    },
    {
      icon: <Wrench className="w-12 h-12" />,
      title: "Pressure Washing",
      description: "Prepare surfaces properly with our professional pressure washing services. Essential for any quality paint job.",
      features: [
        "Residential & commercial",
        "Driveways & walkways",
        "Siding & fences",
        "Mold & mildew removal",
        "Eco-friendly solutions",
        "Pre-paint preparation"
      ],
      image: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=600&h=400&fit=crop"
    },
    {
      icon: <Wrench className="w-12 h-12" />,
      title: "Drywall Repair",
      description: "Professional drywall repair and patching services. We'll make your walls look perfect before painting.",
      features: [
        "Hole & crack repair",
        "Texture matching",
        "Water damage repair",
        "Smooth finish guaranteed",
        "Fast turnaround",
        "Seamless results"
      ],
      image: "https://images.unsplash.com/photo-1621905252507-b35492cc74b4?w=600&h=400&fit=crop"
    },
    {
      icon: <Palette className="w-12 h-12" />,
      title: "Color Consultation",
      description: "Not sure which colors to choose? Our expert color consultants will help you select the perfect palette for your space.",
      features: [
        "Professional color advice",
        "Sample testing",
        "Trend insights",
        "Lighting considerations",
        "Room-by-room planning",
        "Complimentary service"
      ],
      image: "https://images.unsplash.com/photo-1513694203232-719a280e022f?w=600&h=400&fit=crop"
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <PaintingHeader />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1E3A8A] to-[#1E40AF] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-semibold mb-4">
            🎨 Professional Services
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none mb-6">
            Our Painting Services
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            From interior transformations to exterior protection, we offer comprehensive painting solutions for residential and commercial properties throughout Hollywood, FL.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {services.map((service) => (
              <div 
                key={service.title} 
                className={`flex flex-col lg:flex-row gap-8 items-center ${
                  services.indexOf(service) % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                {/* Image */}
                <div className="lg:w-1/2">
                  <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* Content */}
                <div className="lg:w-1/2">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1E3A8A] text-white rounded-2xl mb-6">
                    {service.icon}
                  </div>
                  <h2 className="text-3xl sm:text-4xl font-black text-[#0F172A] mb-4 tracking-tight">
                    {service.title}
                  </h2>
                  <p className="text-lg text-[#64748B] mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {service.features.map((feature) => (
                      <div key={feature} className="flex items-start space-x-2">
                        <CheckCircle className="w-5 h-5 text-[#EA580C] mt-0.5 flex-shrink-0" />
                        <span className="text-[#0F172A]">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-white rounded-full text-sm font-bold text-[#1E3A8A] uppercase tracking-wider mb-4">
              Our Process
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#0F172A] mb-4">
              How We Work
            </h2>
            <p className="text-lg text-[#64748B] max-w-2xl mx-auto">
              A simple, transparent process from quote to completion
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '1', title: 'Free Consultation', desc: 'We visit your property for a detailed assessment' },
              { step: '2', title: 'Quote & Planning', desc: 'Receive a detailed quote and project timeline' },
              { step: '3', title: 'Professional Work', desc: 'Our expert team completes the job on schedule' },
              { step: '4', title: 'Final Walkthrough', desc: "We ensure you're 100% satisfied with the results" }
            ].map((item) => (
              <Card key={item.step} className="text-center border-2 hover:border-[#1E3A8A] transition-colors">
                <CardContent className="p-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#EA580C] text-white rounded-full text-2xl font-black mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold text-[#0F172A] mb-2">
                    {item.title}
                  </h3>
                  <p className="text-[#64748B]">
                    {item.desc}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#1E3A8A] to-[#1E40AF] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            Contact us today for a free, no-obligation quote. Professional service guaranteed!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="w-full sm:w-auto bg-[#EA580C] hover:bg-[#C2410C] text-white text-lg px-8 py-6">
                Request Free Quote
              </Button>
            </Link>
            <a href="tel:+19548172046">
              <Button size="lg" variant="outline" className="w-full sm:w-auto bg-white text-[#1E3A8A] hover:bg-white/90 text-lg px-8 py-6">
                <Phone className="w-5 h-5 mr-2" />
                Call (954) 817-2046
              </Button>
            </a>
          </div>
        </div>
      </section>

      <PaintingFooter />
    </div>
  );
};

export default Services;